import fetchCollectionDisplayPromoProgress from "@faire/web-api/api/product-based-collections/productBasedCollectionToken/active-promo-progress/post";
import getCart, {
  QueryParameters as CartParameters,
} from "@faire/web-api/api/v2/carts/cartToken/get";
import getCartSummary, {
  QueryParameters as CartSummaryParameters,
} from "@faire/web-api/api/v2/carts/cartToken/summary/get";
import { BrandCartsSortBy } from "@faire/web-api/faire/carts/BrandCartsSortBy";
import { IBrandCartV2 } from "@faire/web-api/faire/carts/IBrandCartV2";
import { ICartItemV2 } from "@faire/web-api/faire/carts/ICartItemV2";
import { ICartSummaryResponseV2 } from "@faire/web-api/faire/carts/ICartSummaryResponseV2";
import { ICartV2 } from "@faire/web-api/faire/carts/ICartV2";
import { IGetCartRequest } from "@faire/web-api/faire/carts/IGetCartRequest";
import { IUpdateCartResponseV2 } from "@faire/web-api/faire/carts/IUpdateCartResponseV2";
import { IFilterSection } from "@faire/web-api/indigofair/data/IFilterSection";
import { IInformationMessage } from "@faire/web-api/indigofair/data/IInformationMessage";
import { IRetailerProductBasedCollectionPromoProgressRequest } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionPromoProgressRequest";
import { IRetailerProductBasedCollectionPromoProgressResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionPromoProgressResponse";
import { IRetailerProductBasedCollectionResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionResponse";
import { IShippingMethod } from "@faire/web-api/indigofair/data/IShippingMethod";
import { IWebSocketMessage } from "@faire/web-api/indigofair/websockets/IWebSocketMessage";
import { fulfilledOrStale } from "@faire/web/common/instanceUtils";
import { makeObservable } from "@faire/web/common/makeObservable";
import { mapValues } from "@faire/web/common/mapValues";
import { formatMoney, moneyOrZero } from "@faire/web/common/money";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Message } from "@faire/web/messenger/MessageChannel/MessageChannel";
import cloneDeep from "lodash/cloneDeep";
import { action, computed, observable } from "mobx";
import { IPromiseBasedObservable, fromPromise, PENDING } from "mobx-utils";

import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import {
  generateShipMonthToBrandCartItemsMap,
  PENDING_SHIP_TIME,
} from "@faire/retailer/lib/generateShipMonthToCartItemsMap";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { fromPromiseWithOldValue } from "@faire/retailer/lib/mobxHelpers";
import { webSocketUrls } from "@faire/retailer/lib/webSocketUrls";
import { getCartToken } from "@faire/retailer/serialized-data/getCartToken";
import { getSettingEnablePreCheckoutRetailerEoriCollection } from "@faire/retailer/settings/getSettingEnablePreCheckoutRetailerEoriCollection";
import { CartDataStoreV2 } from "@faire/retailer/stores/domain/CartStore/CartDataStoreV2";
import {
  IBrandCartForUpdate,
  IBrandCartV2WithSummary,
  ICartItemForUpdate,
  ILeanCartItemV2,
  IUpdateItemsOptions,
  UpdateCartItemsHandler,
} from "@faire/retailer/stores/domain/CartStore/consts";
import { FaireProtectionStore } from "@faire/retailer/stores/domain/FaireProtectionStore";
import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { WebSocketChannelStore } from "@faire/retailer/stores/WebSocketChannelStore";

import IRetailerProductBasedCollection = IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;

export class BrandCartViewState {
  /** CartViewState fields */

  @observable
  private cartSummaryResponse?: IPromiseBasedObservable<
    ICartSummaryResponseV2 | undefined
  >;

  @observable
  private filters?: string;

  @observable
  private cartResponse?: IPromiseBasedObservable<ICartV2 | undefined>;

  @observable
  private activeBrandCartsResponse?: IPromiseBasedObservable<
    ICartV2 | undefined
  >;

  @observable
  private savedBrandCartsResponse?: IPromiseBasedObservable<
    ICartV2 | undefined
  >;

  @observable
  private initialLoad: boolean = true;

  @observable
  private collectionDisplayPromoProgressResponse?: IPromiseBasedObservable<IRetailerProductBasedCollectionPromoProgressResponse>;

  private websocket: number | undefined = undefined;

  private shouldExcludeAlcoholDisclaimers: boolean = false;

  constructor(
    private readonly brandToken: string,
    private readonly updateCartFn: UpdateCartItemsHandler,
    private readonly excludeCartSummary?: boolean,
    private readonly updateShippingGroups?: boolean,
    public readonly productBasedCollection?: IRetailerProductBasedCollection
  ) {
    makeObservable(this);
    if (!excludeCartSummary) {
      this.websocket = WebSocketChannelStore.get()
        .getOrInitChannel(webSocketUrls.retailerNotification())
        ?.subscribe(this.handleDutiesCalculatedMessage);
    }
  }

  /** BrandCartViewState getters */

  /**
   * @deprecated Prefer `useBrandCartInitialized()` instead.
   */
  @observable
  initialized = false;

  /**
   * @deprecated Prefer `usePendingCartItems()` instead.
   */
  @observable
  pendingCartItems: Map<string, ICartItemV2> = new Map();

  /**
   * @deprecated Prefer `useCartBrand()` instead.
   */
  @computed
  get cartBrand(): IBrandCartV2 | undefined {
    return this.brandCarts.find((cart) => cart.brand_token === this.brandToken);
  }

  /**
   * @deprecated Prefer `useBrandCartItems()` instead.
   */
  @computed
  get brandCartItems(): ICartItemV2[] {
    return Object.values(this.cartBrand?.cart_items ?? {}).filter(
      isNotUndefined
    );
  }

  /**
   * @deprecated Prefer `useCartBrandInsiderInformationMessage()` instead.
   */
  @computed
  get cartBrandInsiderInformationMessage(): IInformationMessage | undefined {
    return this.brandCarts.find((brandCart: IBrandCartV2) => {
      return brandCart.insider_information_message;
    })?.insider_information_message;
  }

  /**
   * @deprecated Prefer `useCartBrandFreeShippingThresholdInformationMessage()` instead.
   */
  @computed
  get cartBrandFreeShippingThresholdInformationMessage():
    | IInformationMessage
    | undefined {
    return this.brandCarts.find((brandCart: IBrandCartV2) => {
      return brandCart.free_shipping_threshold_information_message;
    })?.free_shipping_threshold_information_message;
  }

  /**
   * @deprecated Prefer `useIsCartEmpty()` instead.
   */
  @computed
  get isCartEmpty() {
    return this.cartBrand === undefined;
  }

  /**
   * @deprecated Prefer `useCartItemMap()` instead.
   */
  @computed
  get cartItemMap(): Map<string, ILeanCartItemV2[]> {
    const items = mapValues(this.cartBrand?.cart_items ?? {});
    if (!items.length && this.pendingCartItems.size === 0) {
      return new Map();
    }
    const pendingItems = Array.from(this.pendingCartItems.values());

    const pending: ICartItemV2[] = pendingItems.filter((item) => {
      const existingCartItem = items.find(
        (cartItem) =>
          cartItem.product_token === item.product_token &&
          cartItem.product_option_token === item.product_option_token
      );
      if (existingCartItem) {
        existingCartItem.quantity = item.quantity;
        existingCartItem.total_price = undefined;
        existingCartItem.total_price_with_promos = undefined;
      }
      return !existingCartItem;
    });

    const shipmentGroups = this.cartBrand?.shipment_groups
      ? this.cartBrand.shipment_groups
      : [];
    const mapWithShipMonth = generateShipMonthToBrandCartItemsMap(
      shipmentGroups,
      items,
      true
    );

    return pending.length > 0
      ? mapWithShipMonth.set(PENDING_SHIP_TIME, pending)
      : mapWithShipMonth;
  }

  /**
   * @deprecated Prefer `useNumberOfItemsInCart()` instead.
   */
  @computed
  get numberOfItemsInCart(): number {
    return mapValues(this.cartBrand?.cart_items ?? {}).length;
  }

  /**
   * @deprecated Prefer `useShowCollectionDisplayPromoTrackingBanner` instead.
   */
  @computed
  get showCollectionDisplayPromoTrackingBanner(): boolean {
    return (
      this.collectionOrderAmountProgress?.minimum_order_amount !== undefined &&
      this.collectionOrderAmountProgress?.current_order_amount !== undefined &&
      !this.isFreeDisplayProductInCart
    );
  }

  /**
   * @deprecated Prefer `useIsQualifiedForCollectionDisplayPromo()` instead.
   */
  @computed
  get isQualifiedForCollectionDisplayPromo(): boolean {
    return MoneyMath.lessOrEqual(
      moneyOrZero(this.collectionOrderAmountProgress?.minimum_order_amount),
      moneyOrZero(this.collectionOrderAmountProgress?.current_order_amount)
    );
  }
  /**
   * @deprecated Prefer `useIsCollectionDisplayPromoActive()` instead.
   */
  get isCollectionDisplayPromoActive(): boolean {
    return (
      this.productBasedCollection?.active_display_promo?.type === "FREE_DISPLAY"
    );
  }

  /**
   * @deprecated Prefer `useRemainingUnlockDisplayPromoAmount()` instead.
   */
  @computed
  get formattedRemainingAmountToUnlockDisplayPromo(): string {
    if (this.isQualifiedForCollectionDisplayPromo) {
      return "";
    }
    return formatMoney({
      money: MoneyMath.subtract(
        moneyOrZero(this.collectionOrderAmountProgress?.minimum_order_amount),
        moneyOrZero(this.collectionOrderAmountProgress?.current_order_amount)
      ),
      omitCentsIfZero: true,
    });
  }

  // This is used to represent initial load
  /**
   * @deprecated Prefer `useIsBrandCartLoading()` instead.
   */
  @computed
  get isLoading(): boolean {
    return (
      (this.isCartSummaryLoading ||
        this.isCartLoading ||
        FaireProtectionStore.get().isLoading) &&
      this.initialLoad
    );
  }

  /**
   * @deprecated Prefer `useHasBrandCartLoaded()` instead.
   */
  @computed
  get hasLoaded(): boolean {
    return (
      !!this.cartResponse &&
      (!!this.cartSummaryResponse || !!this.excludeCartSummary)
    );
  }

  /**
   * @deprecated Prefer `useIsBrandCartError()` instead.
   */
  @computed
  get isError(): boolean {
    return (
      this.cartSummaryResponse?.state === "rejected" ||
      this.cartResponse?.state === "rejected" ||
      this.savedBrandCartsResponse?.state === "rejected" ||
      this.activeBrandCartsResponse?.state === "rejected"
    );
  }

  /**
   * @deprecated Prefer `useImprovedCartBrands()` instead.
   */
  @computed
  get improvedCartBrands(): IBrandCartV2WithSummary[] {
    const retailerHasEori = RetailerStore.get().hasEoriNumber;
    return (this.brandCarts ?? []).map((brand) => {
      return {
        ...brand,
        checkout_summary:
          this.cartSummary?.brand_cart_summaries?.[brand.brand_token ?? ""],
        is_eori_missing:
          brand.retailer_requires_eori &&
          !retailerHasEori &&
          getSettingEnablePreCheckoutRetailerEoriCollection(),
      };
    });
  }

  /** BrandCartViewState private getters */

  @computed
  private get isFreeDisplayProductInCart(): boolean {
    const cartItems = CartDataStoreV2.get().cartItems;
    return Object.values(cartItems).some(
      (cartItem) =>
        cartItem?.product_option_token ===
        this.productBasedCollection?.active_display_promo?.free_display_product
          ?.tile?.selected_product_option_token
    );
  }

  @computed
  private get filterKeys(): string[] | undefined {
    return this.filters?.split(",") ?? [];
  }

  @computed
  private get collectionOrderAmountProgress() {
    return fulfilledOrStale(
      this.collectionDisplayPromoProgressResponse,
      "order_amount_progress"
    )?.order_amount_progress;
  }

  @computed
  private get isCartSummaryLoading(): boolean {
    return (
      !this.cartSummaryResponse || this.cartSummaryResponse.state === PENDING
    );
  }

  @computed
  private get activeCartLoading(): boolean {
    return (
      (!this.activeBrandCartsResponse ||
        this.activeBrandCartsResponse.state === PENDING) &&
      this.isFullCartLoading
    );
  }

  // Cart is considered loaded if we've either loaded the full cart, or loaded the
  // active cart, regardless of whether the saved for later cart is still loading
  @computed
  private get isCartLoading(): boolean {
    return this.isFullCartLoading && this.activeCartLoading;
  }

  @computed
  private get isFullCartLoading(): boolean {
    return !this.cartResponse || this.cartResponse.state === PENDING;
  }

  @computed
  private get cartSummary(): ICartSummaryResponseV2 | undefined {
    return fulfilledOrStale(this.cartSummaryResponse, "brand_cart_summaries");
  }

  @computed
  private get mergedSavedAndActiveCart(): ICartV2 | undefined {
    const activeCart = fulfilledOrStale(
      this.activeBrandCartsResponse,
      "brand_carts"
    );
    const savedCart = fulfilledOrStale(
      this.savedBrandCartsResponse,
      "brand_carts"
    );

    return ICartV2.build({
      token: activeCart?.token,
      brand_carts: this.mergedBrandCarts(activeCart, savedCart),
      filter_sections: this.mergedFilterSections(activeCart, savedCart),
      cart_stats: activeCart?.cart_stats,
      eori_checkout_content: activeCart?.eori_checkout_content,
    });
  }

  @computed
  private get cartData(): ICartV2 | undefined {
    const fullCartData = fulfilledOrStale(this.cartResponse, "brand_carts");
    if (fullCartData || this.activeCartLoading) {
      return fullCartData;
    }
    return this.mergedSavedAndActiveCart;
  }

  @computed
  private get brandCarts(): IBrandCartV2[] {
    return this.cartData?.brand_carts ?? [];
  }

  @computed
  private get filterSections(): IFilterSection[] {
    return this.cartData?.filter_sections ?? [];
  }

  /** BrandCartViewState methods */

  getCartItemTokenByProductToken = (productToken: string): string => {
    let result = "";
    if (this.cartBrand) {
      result =
        Object.values(this.cartBrand.cart_items).find((cart_item) => {
          return cart_item?.product_token === productToken;
        })?.token ?? "";
    }
    return result;
  };

  @action
  cleanup = () => {
    if (this.websocket !== undefined) {
      WebSocketChannelStore.get()
        .getOrInitChannel(webSocketUrls.retailerNotification())
        ?.unsubscribe(this.websocket);
    }
  };

  /**
   * @deprecated Prefer `useSetPendingCartItems()` instead.
   */
  @action
  setPendingCartItems = (item: ICartItemV2) => {
    const key = (item.product_token ?? "") + (item.product_option_token ?? "");
    const existingItem = this.pendingCartItems.get(key);
    if (existingItem) {
      existingItem.quantity = item.quantity;
    } else {
      this.pendingCartItems.set(key, item);
    }
  };

  /**
   * @deprecated Prefer `useFetchDisplayPromoProgress()` instead.
   */
  @action
  fetchDisplayPromoProgress = () => {
    if (
      this.isCollectionDisplayPromoActive &&
      canAddToCart() &&
      this.productBasedCollection?.token
    ) {
      this.collectionDisplayPromoProgressResponse = fromPromiseWithOldValue(
        fetchCollectionDisplayPromoProgress(
          this.productBasedCollection.token,
          IRetailerProductBasedCollectionPromoProgressRequest.build({
            promo_token:
              this.productBasedCollection.active_display_promo!.token,
          })
        ),
        this.collectionDisplayPromoProgressResponse
      );
    }
  };

  /** Private BrandCartViewState methods */
  @action
  private clearPendingCartItems = () => {
    this.pendingCartItems.clear();
  };

  private getCartRequest = (savedForLater?: boolean): IGetCartRequest =>
    IGetCartRequest.build({
      filter_brand_carts_by_keys: this.filterKeys,
      brand_tokens: [this.brandToken],
      sort_brand_carts_by: BrandCartsSortBy.UPDATED_AT,
      exclude_alcohol_disclaimer: this.shouldExcludeAlcoholDisclaimers,
      saved_for_later: savedForLater,
      should_collapse_preorder_groups: this.updateShippingGroups,
    });

  @action
  init = (
    options?: { filters?: string },
    parallelizeSavedAndActiveBrands?: boolean
  ) => {
    if (!isLoggedInRetailer()) {
      return;
    }

    this.initialized = true;

    this.filters = options?.filters;

    FaireProtectionStore.get().fetchData();

    if (!this.excludeCartSummary) {
      this.cartSummaryResponse = fromPromise(
        getCartSummary(
          getCartToken(),
          CartSummaryParameters.build({
            brand_tokens: [this.brandToken],
          })
        ).catch(() => {
          return undefined;
        })
      );
    }

    this.fetchDisplayPromoProgress();

    // Filtering by brand cart tokens takes precedence on the backend
    // over filtering by active or saved brand cart state
    if (!parallelizeSavedAndActiveBrands || this.brandToken) {
      this.cartResponse = fromPromise(
        getCart(
          getCartToken(),
          CartParameters.build(this.getCartRequest())
        ).catch(() => {
          return undefined;
        })
      );
      return;
    }

    this.savedBrandCartsResponse = fromPromise(
      getCart(
        getCartToken(),
        CartParameters.build(this.getCartRequest(true))
      ).catch(() => {
        return undefined;
      })
    );

    this.activeBrandCartsResponse = fromPromise(
      getCart(getCartToken(), CartParameters.build(this.getCartRequest(false)))
        .then((data) => {
          return data;
        })
        .catch(() => {
          return undefined;
        })
    );
  };

  @action
  private updateCartSummary = async () => {
    if (!isLoggedInRetailer()) {
      return;
    }

    this.initialLoad = false;

    if (this.excludeCartSummary) {
      return;
    }
    const oldSummary = this.cartSummaryResponse?.case<ICartSummaryResponseV2>(
      {}
    );
    const promise = getCartSummary(
      getCartToken(),
      CartSummaryParameters.build({
        brand_tokens: [this.brandToken],
      })
    ).catch(() => {
      return oldSummary;
    });

    this.cartSummaryResponse = fromPromiseWithOldValue(
      promise,
      this.cartSummaryResponse
    );

    return promise;
  };

  @action
  refreshCart = async (options?: {
    selectedShippingMethodType?: keyof typeof IShippingMethod.ShippingMethodType;
    filters?: string;
  }) => {
    if (!isLoggedInRetailer()) {
      return;
    }
    this.filters = options?.filters;

    const oldCart = this.cartResponse?.case<ICartV2>({});

    const promise = getCart(
      getCartToken(),
      CartParameters.build({
        filter_brand_carts_by_keys: this.filterKeys,
        brand_tokens: [this.brandToken],
        sort_brand_carts_by: BrandCartsSortBy.UPDATED_AT,
        shipping_method_type: options?.selectedShippingMethodType,
        exclude_alcohol_disclaimer: this.shouldExcludeAlcoholDisclaimers,
        should_collapse_preorder_groups: this.updateShippingGroups,
      })
    ).catch(() => {
      return oldCart;
    });

    this.cartResponse = fromPromiseWithOldValue(promise, this.cartResponse);
  };

  private getUpdatedCarts = (
    items: ICartItemForUpdate[],
    updatedCartResponse?: IUpdateCartResponseV2
  ) => {
    const currentFilterSections = cloneDeep(this.filterSections);
    const updatedOrDeletedBrandTokens = new Set();

    items.forEach((item) => {
      updatedOrDeletedBrandTokens.add(item.brand_token);
    });
    updatedCartResponse?.brand_carts.forEach((cart) => {
      updatedOrDeletedBrandTokens.add(cart.brand_token);
    });
    const activeCarts = this.cartData?.brand_carts?.filter(
      (cart) => !updatedOrDeletedBrandTokens.has(cart.brand_token)
    );
    const brandCarts = [
      ...(updatedCartResponse?.brand_carts ?? []),
      ...(activeCarts ?? []),
    ];
    this.clearPendingCartItems();
    return ICartV2.build({
      cart_stats: this.cartData?.cart_stats,
      token: updatedCartResponse?.token,
      brand_carts: brandCarts,
      filter_sections: currentFilterSections,
    });
  };

  private updateCartData = async (
    items: ICartItemForUpdate[],
    options: IUpdateItemsOptions,
    brand_carts?: IBrandCartForUpdate[],
    requested_ship_dates?: Record<string, number>
  ) => {
    const oldCart = this.cartResponse?.case<ICartV2>({});

    const promise = this.updateCartFn({
      items,
      allowEmptyItems: true,
      brand_carts,
      requested_ship_dates,
      should_collapse_preorder_groups: this.updateShippingGroups,
      upltParams: options.upltParams,
    })
      .then((response) => this.getUpdatedCarts(items, response))
      .catch(() => {
        this.clearPendingCartItems();
        return oldCart;
      });

    this.cartResponse = fromPromiseWithOldValue(promise, this.cartResponse);
    return promise;
  };

  @action
  updateItems = async (
    items: ICartItemForUpdate[],
    options: IUpdateItemsOptions,
    brandCarts?: IBrandCartForUpdate[]
  ) => {
    this.initialLoad = false;
    await this.updateCartData(items, options, brandCarts);
    await this.updateCartSummary();
    this.fetchDisplayPromoProgress();
    this.clearPendingCartItems();
  };

  private handleDutiesCalculatedMessage = (message: Message) => {
    // When cart is updated, BE calls a 3rd party api to calculate related
    // duties, and send a websocket message when finishes
    if (message.type === IWebSocketMessage.Type.CART_DUTIES_UPDATED) {
      this.refreshCart();
      this.updateCartSummary();
    }
  };

  private mergeCartArrays = <T>(
    activeItems: T[] | undefined,
    savedItems: T[] | undefined,
    getKey: (item: T) => string | undefined
  ): T[] => {
    const uniqueItemsMap = new Map<string | undefined, T>();

    (activeItems ?? []).forEach((item) => {
      uniqueItemsMap.set(getKey(item), item);
    });

    (savedItems ?? []).forEach((item) => {
      if (!uniqueItemsMap.has(getKey(item))) {
        uniqueItemsMap.set(getKey(item), item);
      }
    });

    return Array.from(uniqueItemsMap.values());
  };

  private mergedBrandCarts = (
    activeCart: ICartV2 | undefined,
    savedCart: ICartV2 | undefined
  ) => {
    return this.mergeCartArrays(
      activeCart?.brand_carts,
      savedCart?.brand_carts,
      (cart) => cart.brand_token
    );
  };

  private mergedFilterSections = (
    activeCart: ICartV2 | undefined,
    savedCart: ICartV2 | undefined
  ) => {
    return this.mergeCartArrays(
      activeCart?.filter_sections,
      savedCart?.filter_sections,
      (section) => section.field_name
    );
  };
}
